import * as Sentry from '@sentry/vue';
import type { App } from 'vue';

import { useToast } from '@/components/ui/toast/use-toast';

export function initSentry(app: App) {
    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (!dsn) return;

    Sentry.init({
        app,
        dsn,
        integrations: [
            Sentry.feedbackIntegration({
                colorScheme: 'system',
                autoInject: false,
                showBranding: false,
                formTitle: 'Contact us',
                submitButtonLabel: 'Send',
                messagePlaceholder: "Tell us what's on your mind..."
            })
        ]
    });
}

export const isFeedbackEnabled = () => Boolean(Sentry.getFeedback());

/* Sentry's own class names, which no custom property exposes: the shadow root is
open, so the sheet has to be adopted from here rather than from a stylesheet. */
const shadowStyles = new CSSStyleSheet();
shadowStyles.replaceSync(`
    .btn-group { grid-auto-flow: column; justify-content: end; }
    .btn--primary { order: 1; }
    .btn { transition: background-color 150ms, color 150ms; }
    /* Sentry scrolls these, which clips the focus ring: the card scrolls too. */
    .form,
    .form__right { overflow: visible; }
    .form__input:focus-visible,
    .btn:focus-visible,
    .app-dialog-close:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--feedback-surface), 0 0 0 4px hsl(var(--ring));
    }
    .app-dialog-close {
        align-self: center;
        padding: 0;
        border: none;
        background: none;
        color: inherit;
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 150ms;
    }
    .app-dialog-close:hover { opacity: 1; }
    .app-dialog-close svg { display: block; width: 16px; height: 16px; }
`);

function adoptShadowStyles() {
    const shadow = document.getElementById('sentry-feedback')?.shadowRoot;

    if (!shadow || shadow.adoptedStyleSheets.includes(shadowStyles)) return;

    shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, shadowStyles];
}

/* The form has no close button of its own, and no option adds one. */
function addCloseButton(onClose: () => void) {
    const header = document
        .getElementById('sentry-feedback')
        ?.shadowRoot?.querySelector('.dialog__header');

    if (!header || header.querySelector('.app-dialog-close')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'app-dialog-close';
    button.setAttribute('aria-label', 'Close');
    button.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>';
    button.addEventListener('click', onClose);
    header.append(button);
}

export async function openFeedbackForm() {
    const feedback = Sentry.getFeedback();

    if (!feedback) return;

    const { toast } = useToast();
    // Removing the form drops Sentry's own success message, which the app toast
    // replaces, and keeps closed dialogs from piling up in the shadow root.
    const form = await feedback.createForm({
        onFormClose: () => form.removeFromDom(),
        onSubmitSuccess: () => {
            form.removeFromDom();
            toast({
                title: 'Thank you for your message!',
                duration: 3000
            });
        }
    });

    form.appendToDom();
    adoptShadowStyles();
    form.open();
    addCloseButton(() => form.removeFromDom());
}
